import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { analytics } from '../lib/analytics';
import api from '../lib/api';
import { CheckCircle, SpinnerGap } from '@phosphor-icons/react';

export default function BookingConfirmation() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [conversionTracked, setConversionTracked] = useState(false);

  useEffect(() => {
    if (!sessionId) { 
      setStatus('error');
      analytics.trackEvent('confirmation_error', { error: 'no_session_id', bookingId });
      return;
    }
    
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setStatus('success');
          
          // Tracker la conversion une seule fois
          if (!conversionTracked && bookingId) {
            setConversionTracked(true);
            
            // Récupérer les détails de la réservation pour le tracking
            try {
              const bookingResponse = await api.get(`/bookings/${bookingId}`);
              const booking = bookingResponse.data;
              setBookingDetails(booking);
              
              // Tracking de conversion avec plus de détails
              analytics.trackConversion(bookingId, data.amount_total);
              analytics.trackEvent('payment_success', {
                bookingId: bookingId,
                amount: data.amount_total,
                currency: data.currency,
                vehicleType: booking?.vehicleType,
                distanceKm: booking?.distanceKm,
                tripType: booking?.tripType
              });
            } catch (error) {
              // Tracking même sans détails
              analytics.trackConversion(bookingId, data.amount_total);
              analytics.trackEvent('payment_success', {
                bookingId: bookingId,
                amount: data.amount_total,
                currency: data.currency
              });
            }
          }
        } else if (data.status === 'expired') {
          setStatus('error');
          analytics.trackEvent('payment_expired', { sessionId, bookingId });
        } else {
          if (attempts < 10) {
            setTimeout(() => setAttempts(a => a + 1), 2000);
          } else {
            setStatus('pending');
            analytics.trackEvent('payment_pending', { sessionId, bookingId, attempts });
          }
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
        analytics.trackEvent('payment_status_error', { 
          error: error.message, 
          sessionId, 
          bookingId,
          attempts 
        });
      }
    };
    
    poll();
  }, [sessionId, attempts, bookingId, conversionTracked]);

  // Tracker la visualisation de la page
  useEffect(() => {
    analytics.trackPageView('/booking/confirmation', {
      sessionId,
      bookingId,
      referrer: document.referrer
    });
  }, [sessionId, bookingId]);

  const handleViewBookingClick = () => {
    analytics.trackEvent('view_booking_click', { bookingId });
  };

  const handleBackHomeClick = () => {
    analytics.trackEvent('back_home_from_confirmation', { status, bookingId });
  };

  return (
    <div className="pt-20 min-h-screen flex items-center justify-center" data-testid="booking-confirmation-page">
      <div className="max-w-md mx-auto px-6 text-center">
        {status === 'checking' && (
          <div data-testid="confirmation-checking">
            <SpinnerGap size={48} className="text-desti-gold mx-auto mb-4 animate-spin" />
            <h2 className="font-heading text-2xl text-white">Vérification du paiement...</h2>
            <p className="text-sm text-desti-text mt-2">Merci de patienter quelques instants.</p>
            <p className="text-xs text-white/30 mt-4">Tentative {attempts + 1}/10</p>
          </div>
        )}
        
        {status === 'success' && (
          <div data-testid="confirmation-success">
            <CheckCircle size={64} weight="duotone" className="text-desti-gold mx-auto mb-6" />
            <h2 className="font-heading text-3xl text-white mb-3">Réservation confirmée !</h2>
            <p className="text-sm text-desti-text mb-2">Votre paiement a été accepté avec succès.</p>
            {bookingId && (
              <p className="text-xs text-white/40 mb-2">
                Réservation #{bookingId.slice(-8).toUpperCase()}
              </p>
            )}
            {bookingDetails && (
              <div className="bg-desti-surface border border-white/10 rounded-lg p-4 mb-6 text-left">
                <p className="text-xs text-desti-gold uppercase tracking-wide mb-2">Récapitulatif</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/60">Trajet :</span>
                    <span className="text-white">{bookingDetails.distanceKm} km</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Véhicule :</span>
                    <span className="text-white">{bookingDetails.vehicleName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Date :</span>
                    <span className="text-white">{bookingDetails.pickupDate} à {bookingDetails.pickupTime}</span>
                  </div>
                  <div className="flex justify-between border-t border-white/10 pt-2 mt-2">
                    <span className="text-desti-gold font-medium">Total TTC :</span>
                    <span className="text-desti-gold font-medium">{bookingDetails.priceTTC} €</span>
                  </div>
                </div>
              </div>
            )}
            <p className="text-sm text-desti-text mb-8">
              Un email de confirmation vous a été envoyé.
            </p>
            <div className="flex flex-col gap-3">
              <Link 
                to="/espace-membre/reservations" 
                onClick={handleViewBookingClick}
                className="bg-desti-gold text-desti-black font-medium py-3 px-6 text-sm tracking-wide hover:bg-desti-gold-hover transition-colors text-center" 
                data-testid="view-booking-btn"
              >
                Voir ma réservation
              </Link>
              <Link 
                to="/" 
                onClick={handleBackHomeClick}
                className="border border-white/20 text-white py-3 px-6 text-sm tracking-wide hover:bg-white/5 transition-colors text-center" 
                data-testid="back-home-btn"
              >
                Retour à l'accueil
              </Link>
            </div>
          </div>
        )}
        
        {status === 'pending' && (
          <div data-testid="confirmation-pending">
            <SpinnerGap size={48} className="text-desti-gold mx-auto mb-4 animate-spin" />
            <h2 className="font-heading text-2xl text-white mb-3">Paiement en cours de traitement</h2>
            <p className="text-sm text-desti-text mb-2">
              Votre paiement est en cours de vérification par Stripe.
            </p>
            <p className="text-xs text-white/40 mb-6">
              Vous recevrez un email de confirmation sous quelques minutes.
            </p>
            <div className="flex flex-col gap-3">
              <Link 
                to="/espace-membre/reservations" 
                onClick={handleViewBookingClick}
                className="border border-white/20 text-white py-3 px-6 text-sm tracking-wide hover:bg-white/5 transition-colors text-center" 
                data-testid="check-bookings-btn"
              >
                Voir mes réservations
              </Link>
              <Link 
                to="/" 
                onClick={handleBackHomeClick}
                className="bg-desti-gold text-desti-black font-medium py-3 px-6 text-sm tracking-wide hover:bg-desti-gold-hover transition-colors text-center" 
                data-testid="back-home-pending-btn"
              >
                Retour à l'accueil
              </Link>
            </div>
          </div>
        )}
        
        {status === 'error' && (
          <div data-testid="confirmation-error">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <span className="text-red-500 text-3xl">!</span>
            </div>
            <h2 className="font-heading text-2xl text-white mb-3">Un problème est survenu</h2>
            <p className="text-sm text-desti-text mb-2">
              Nous n'avons pas pu confirmer votre paiement.
            </p>
            <p className="text-xs text-white/40 mb-6">
              Veuillez vérifier votre email ou contacter notre service client.
            </p>
            <div className="flex flex-col gap-3">
              <Link 
                to="/" 
                onClick={handleBackHomeClick}
                className="bg-desti-gold text-desti-black font-medium py-3 px-6 text-sm tracking-wide hover:bg-desti-gold-hover transition-colors text-center" 
                data-testid="back-home-error-btn"
              >
                Retour à l'accueil
              </Link>
              <a 
                href="mailto:destidriving@gmail.com"
                className="border border-white/20 text-white py-3 px-6 text-sm tracking-wide hover:bg-white/5 transition-colors text-center"
                data-testid="contact-support-btn"
              >
                Contacter le support
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}